<template>
    <ds-modal
        :is-open="isOpen"
        class="rename-modal"
        size="med"
        dialog
        show-close
        data-qa="modal-title"
        :title="title"
        @close="close"
    >
        <form data-qa="choose-site-name-form" novalidate @submit.prevent="rename">
            <ds-input-field
                v-model="name"
                class="choose-site-name-modal-form validation"
                :class="{'dirty': (nameOverLimit || !name)}"
                data-qa="site-name"
                :maxlength="maxLength"
                :invalid="nameOverLimit"
                required
                :label="nameInputLabel"
                :submitted="nameOverLimit || !name"
            >
                <template #help>
                    <div class="name-footer">
                        <p v-if="nameOverLimit" class="limit-warning">
                            {{ limitExceededErrorMessage }}
                        </p>
                        <p v-else>
                            <span v-if="name" class="helper-text">  {{ helperText }} </span>
                            <span v-else class="limit-warning">
                                {{ noEmptyErrorMessage }}
                            </span>
                        </p>
                        <p :class="{'dirty': (nameOverLimit || !name)}" class="character-count">
                            {{ characterCount }}
                        </p>
                    </div>
                </template>
            </ds-input-field>

            <div class="button-row">
                <ds-spinner v-if="isLoading" data-qa="loading-spinner" :size="40" />
                <ds-filled-button
                    v-else
                    type="submit"
                    data-qa="site-name-button"
                    @click="rename"
                >
                    Continue
                </ds-filled-button>
            </div>
            <error-banner v-if="showErrorBanner" class="error-banner" :message="errorMessage" />
        </form>
    </ds-modal>
</template>

<script>
import ErrorBanner from './ErrorBanner';

export const NO_EMPTY_NAME_ERROR = 'Page name is required';
export const LIMIT_EXCEEDED_ERROR_MESSAGE = 'Can’t exceed 40 characters';
export const NAME_LENGTH_ERROR = 'The name of your landing page must be 40 characters or less.';

const SITE_NAME_MAX_LENGTH = 80;
const MAX_SIZE_NAME_FORM_SUBMISSION = 40;

export default {
    NO_EMPTY_NAME_ERROR,
    NAME_LENGTH_ERROR,
    LIMIT_EXCEEDED_ERROR_MESSAGE,
    components: {
        ErrorBanner,
    },

    props: {
        initialName: {
            type: String,
            default: '',
        },
        isOpen: Boolean,
        title: String,
        nameInputLabel: String,
        helperText: String,
        validate: {
            type: Boolean,
            default: true,
        },
    },

    data() {
        return {
            name: this.initialName || 'Landing Page',
            isLoading: false,
            showErrorBanner: false,
        };
    },

    watch: {
        // When the modal is closed, reset the button spinner state
        isOpen(newValue) {
            if (!newValue) {
                this.isLoading = false;
            }
        },

        initialName(newValue) {
            this.name = newValue;
        },

        name() {
            this.showErrorBanner = false;
        },
    },

    computed: {
        isNameInvalid() {
            return !this.name;
        },

        characterCount() {
            return `${this.name.length}/${MAX_SIZE_NAME_FORM_SUBMISSION}`;
        },

        maxLength() {
            return SITE_NAME_MAX_LENGTH;
        },

        noEmptyErrorMessage() {
            return NO_EMPTY_NAME_ERROR;
        },

        limitExceededErrorMessage() {
            return LIMIT_EXCEEDED_ERROR_MESSAGE;
        },

        nameOverLimit() {
            return this.validate && this.name.length > MAX_SIZE_NAME_FORM_SUBMISSION;
        },

        errorMessage() {
            if (this.nameOverLimit) {
                return NAME_LENGTH_ERROR;
            }

            if (!this.name) {
                return NO_EMPTY_NAME_ERROR;
            }

            return null;
        },
    },

    methods: {
        close() {
            this.$emit('close');
        },

        rename() {
            if (this.name && !this.nameOverLimit) {
                this.isLoading = true;
                this.showErrorBanner = false;
                this.$emit('rename', this.name);
            } else {
                this.showErrorBanner = true;
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.name-footer {
    display: flex;
    justify-content: space-between;
    font-size: $font-size-xs;
}

.choose-site-name-modal-form {
    margin-bottom: 0;
}

.limit-warning {
    color: $color-red;
}

.character-count {
    color: $color-ink-600;
}

.error-banner {
    margin: $spacing-200 0 0;
    padding: $spacing-200 $spacing-200;
    border: 0;
}

.character-count.dirty {
    color: $color-red;
}

:deep(.validation.dirty input) {
    border: 1px solid  $color-red;
}

:deep(.validation.dirty input:focus + label) {
    color: $color-red;
}

:deep(.validation.dirty label) {
    color: $color-red;
}
</style>
